import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
    Copy, Plus, Pencil, Trash2, Puzzle, Eye, Code2, BarChart3,
    MousePointerClick, CheckCircle2, AlertTriangle, Info, Globe
} from 'lucide-react';
import { toast } from 'sonner';

// ─── CANONICAL PLATFORM URL ───────────────────────────────────────────────────
// This is the ONLY URL used in all generated embed codes.
// Never use window.location.origin or process.env.REACT_APP_BACKEND_URL here.
const PLATFORM_URL = 'https://oklovendo.okgo.mx';

// ─── helpers ──────────────────────────────────────────────────────────────────

/** Escape HTML special chars so they're safe inside attribute values. */
function escAttr(str = '') {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/** Validate that a string is a safe https URL (no XSS vectors). */
function isSafeUrl(str) {
    try {
        const u = new URL(str);
        return u.protocol === 'https:' || u.protocol === 'http:';
    } catch { return false; }
}

/** Clamp a number between min and max. */
const clamp = (v, min, max) => Math.max(min, Math.min(max, Number(v) || min));

/**
 * Build the dimension string for width/height.
 * unit = 'px' → "380px"   unit = '%' → "80%"   unit = 'vw'/'vh' → "100vw"
 */
function dimStr(value, unit) {
    const v = clamp(value, 1, unit === 'px' ? 9999 : 100);
    return `${v}${unit}`;
}

// ─── embed code generators ────────────────────────────────────────────────────

/**
 * Generates a robust, sanitized <iframe> embed snippet.
 * - Uses PLATFORM_URL (never dev origin)
 * - Validates widget id format (alphanumeric / uuid)
 * - Applies sandbox with minimal required permissions
 * - Proper allow, referrerpolicy, loading attributes
 * - Escapes name into title attribute
 */
function buildIframe(w) {
    const cfg     = w.config || {};
    const id      = String(w.id || '').replace(/[^a-zA-Z0-9_-]/g, ''); // sanitize id
    const src     = `${PLATFORM_URL}/widget/${id}`;
    const radius  = clamp(cfg.radius ?? 16, 0, 64);
    const width   = dimStr(cfg.width  ?? 380, cfg.width_unit  || 'px');
    const height  = dimStr(cfg.height ?? 520, cfg.height_unit || 'px');
    const name    = escAttr(w.name || 'OkWidget');

    if (!isSafeUrl(src)) return '<!-- Error: URL de widget inválida -->';

    return `<!-- OkLoVendo Widget · oklovendo.okgo.mx -->
<iframe
  src="${src}"
  width="${width}"
  height="${height}"
  style="border:0;border-radius:${radius}px;display:block;max-width:100%"
  title="${name}"
  allow="clipboard-write"
  referrerpolicy="no-referrer-when-downgrade"
  loading="lazy"
  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
></iframe>`;
}

/**
 * Generates a robust async script loader snippet.
 * - Uses PLATFORM_URL (never dev origin)
 * - Guards against double-load (idempotent)
 * - onerror handler logs clearly
 * - No eval, no inline JS, no data: URIs
 */
function buildScript(w) {
    const id  = String(w.id || '').replace(/[^a-zA-Z0-9_-]/g, '');
    const src = `${PLATFORM_URL}/widget-loader.js`;

    if (!id) return '<!-- Error: widget sin ID -->';

    return `<!-- OkLoVendo Widget Loader · oklovendo.okgo.mx -->
<div id="ok-widget-${id}"></div>
<script>
(function(d, wid, loaderSrc) {
  // Prevent double-initialization
  if (d.getElementById('okwl-' + wid)) return;
  var el = d.getElementById('ok-widget-' + wid);
  if (!el) { console.warn('[OkWidget] Contenedor #ok-widget-' + wid + ' no encontrado.'); return; }
  var s = d.createElement('script');
  s.id    = 'okwl-' + wid;
  s.async = true;
  s.defer = true;
  s.src   = loaderSrc + '?wid=' + encodeURIComponent(wid) + '&t=' + Date.now();
  s.onerror = function() {
    console.error('[OkWidget] No se pudo cargar el widget "' + wid + '". Verifica tu conexión o contacta soporte en oklovendo.okgo.mx');
  };
  (d.head || d.documentElement).appendChild(s);
})(document, '${id}', '${src}');
</script>`;
}

// ─── constants ────────────────────────────────────────────────────────────────

const WIDGET_TYPES = [
    { value: 'user_ads',           label: 'Anuncios de un vendedor',                        needs_user: true,  is_ad: true  },
    { value: 'featured_ads',       label: 'Anuncios destacados del marketplace (grilla)',    needs_user: false, is_ad: true  },
    { value: 'ad_carousel',        label: 'Carrusel horizontal de anuncios',                needs_user: false, is_ad: true  },
    { value: 'user_carousel',      label: 'Carrusel de tarjetas de usuarios',               needs_user: false, is_user_carousel: true },
    { value: 'search_bar',         label: 'Buscador embebido',                             needs_user: false },
    { value: 'rating_summary',     label: 'Resumen de calificaciones de vendedor',          needs_user: true  },
    { value: 'single_ad',          label: 'Anuncio individual (card)',                      needs_user: false, needs_ad: true },
    { value: 'user_panel_shortcut',label: 'Accesos rápidos al panel de usuario',            needs_user: false },
];

const ORIGINS = [
    { value: 'home',        label: 'Home de la plataforma' },
    { value: 'user_profile',label: 'Perfil público de usuario' },
    { value: 'user_panel',  label: 'Panel de usuario' },
];

const DIM_UNITS = ['px', '%', 'vw', 'vh'];

const blank = () => ({
    name: '',
    widget_type: 'featured_ads',
    origin: 'home',
    target_user_id: null,
    target_ad_id: null,
    enabled: true,
    description: '',
    config: {
        theme: 'light',
        primary_color: '#dc2626',
        radius: 16,
        width: 380,
        width_unit: 'px',
        height: 520,
        height_unit: 'px',
        items: 6,
        filters: { category: null, seller_id: null, min_price: null, max_price: null },
        autoplay: false,
        carousel: false,
        carousel_orientation: 'horizontal',
        show_logo: true,
        user_carousel_kind: 'new',
        cta_label: 'Ver más',
        cta_url: '',
        allowed_domains: [],
        show_title: true,
        title: '',
    },
});

// ─── main component ───────────────────────────────────────────────────────────

export default function AdminWidgets() {
    const [widgets,    setWidgets]    = useState([]);
    const [users,      setUsers]      = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [editing,    setEditing]    = useState(null);
    const [deleteId,   setDeleteId]   = useState(null);
    const [preview,    setPreview]    = useState(null);
    const [embedFor,   setEmbedFor]   = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [w, us, cs] = await Promise.all([
                api.get('/admin/widgets'),
                api.get('/admin/users?limit=500').catch(() => ({ data: [] })),
                api.get('/categories').catch(() => ({ data: [] })),
            ]);
            setWidgets(w.data);
            setUsers(Array.isArray(us.data) ? us.data : (us.data.items || []));
            setCategories(Array.isArray(cs.data) ? cs.data : (cs.data.items || []));
        } catch { toast.error('Error cargando widgets'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editing) return;
        if (!editing.name.trim()) { toast.error('Nombre requerido'); return; }
        const typeSpec = WIDGET_TYPES.find(t => t.value === editing.widget_type);
        if (typeSpec?.needs_user && !editing.target_user_id) { toast.error('Selecciona un usuario objetivo'); return; }
        if (typeSpec?.needs_ad  && !editing.target_ad_id)   { toast.error('Ingresa el ID del anuncio'); return; }
        try {
            const body = {
                name: editing.name.trim(),
                widget_type: editing.widget_type,
                origin: editing.origin,
                target_user_id: editing.target_user_id || null,
                target_ad_id:   editing.target_ad_id   || null,
                enabled: !!editing.enabled,
                description: editing.description || '',
                config: editing.config || {},
            };
            if (editing.id) {
                await api.put(`/admin/widgets/${editing.id}`, body);
                toast.success('Widget actualizado');
            } else {
                await api.post('/admin/widgets', body);
                toast.success('Widget creado');
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error guardando widget'); }
    };

    const doDelete = async () => {
        try {
            await api.delete(`/admin/widgets/${deleteId}`);
            toast.success('Widget eliminado');
            setDeleteId(null);
            load();
        } catch { toast.error('Error al eliminar'); }
    };

    const copy = (text) => {
        navigator.clipboard.writeText(text).then(
            () => toast.success('Copiado al portapapeles'),
            () => toast.error('No se pudo copiar — copia el texto manualmente'),
        );
    };

    // Preview iframe src — uses PLATFORM_URL for production correctness
    const previewSrc = (w) => `${PLATFORM_URL}/widget/${String(w.id).replace(/[^a-zA-Z0-9_-]/g, '')}`;

    return (
        <div className="space-y-5" data-testid="admin-widgets-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-xl sm:text-2xl font-semibold tracking-tight flex items-center gap-2">
                        <Puzzle className="h-5 w-5 text-red-600" /> Widgets
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Crea widgets embebibles para compartir en sitios externos. Todos los códigos apuntan a{' '}
                        <a href={PLATFORM_URL} target="_blank" rel="noreferrer" className="text-red-600 underline font-medium">{PLATFORM_URL}</a>.
                    </p>
                </div>
                <Button onClick={() => setEditing(blank())} className="bg-red-600 hover:bg-red-700" data-testid="new-widget-btn">
                    <Plus className="h-4 w-4 mr-1" /> Nuevo widget
                </Button>
            </div>

            {/* Platform URL notice */}
            <div className="flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                <Globe className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                <span>
                    Los códigos de embed generados siempre apuntan a{' '}
                    <strong>{PLATFORM_URL}</strong>. Esta es la URL canónica de producción de la plataforma.
                </span>
            </div>

            {/* Widget list */}
            {loading ? (
                <div className="text-sm text-muted-foreground">Cargando…</div>
            ) : widgets.length === 0 ? (
                <div className="rounded-2xl border border-dashed p-10 text-center bg-card">
                    <Puzzle className="h-10 w-10 mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mt-3">Aún no hay widgets. Crea el primero.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="widgets-list">
                    {widgets.map(w => {
                        const type   = WIDGET_TYPES.find(t => t.value === w.widget_type);
                        const origin = ORIGINS.find(o => o.value === w.origin);
                        const cfg    = w.config || {};
                        const wStr   = dimStr(cfg.width  ?? 380, cfg.width_unit  || 'px');
                        const hStr   = dimStr(cfg.height ?? 520, cfg.height_unit || 'px');
                        return (
                            <div key={w.id} className="rounded-2xl border bg-card p-4" data-testid={`widget-${w.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-semibold truncate">{w.name}</div>
                                            {w.enabled
                                                ? <Badge className="bg-emerald-600">Activo</Badge>
                                                : <Badge variant="outline">Inactivo</Badge>
                                            }
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            {type?.label || w.widget_type} · {origin?.label}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                                            {wStr} × {hStr} · r={cfg.radius ?? 16}px
                                        </div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <Button variant="ghost" size="sm" onClick={() => setPreview(w)}   data-testid={`preview-${w.id}`} title="Vista previa"><Eye      className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="sm" onClick={() => setEmbedFor(w)} data-testid={`embed-${w.id}`}   title="Código embed"><Code2   className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="sm" onClick={() => setEditing({ ...w })} data-testid={`edit-${w.id}`}><Pencil  className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="sm" onClick={() => setDeleteId(w.id)}    data-testid={`delete-${w.id}`}><Trash2  className="h-4 w-4 text-red-600" /></Button>
                                    </div>
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                                    <div className="bg-secondary/40 rounded p-2 flex items-center gap-1">
                                        <BarChart3 className="h-3 w-3 text-red-600 flex-shrink-0" />
                                        <div><div className="text-[10px] text-muted-foreground">Impresiones</div><div className="font-semibold">{w.impressions ?? 0}</div></div>
                                    </div>
                                    <div className="bg-secondary/40 rounded p-2 flex items-center gap-1">
                                        <MousePointerClick className="h-3 w-3 text-red-600 flex-shrink-0" />
                                        <div><div className="text-[10px] text-muted-foreground">Clicks</div><div className="font-semibold">{w.clicks ?? 0}</div></div>
                                    </div>
                                    <div className="bg-secondary/40 rounded p-2">
                                        <div className="text-[10px] text-muted-foreground">CTR</div>
                                        <div className="font-semibold">{w.impressions ? ((w.clicks / w.impressions) * 100).toFixed(1) + '%' : '—'}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── Editor dialog ── */}
            <WidgetEditor
                open={!!editing}
                value={editing}
                onChange={setEditing}
                onSave={save}
                users={users}
                categories={categories}
            />

            {/* ── Delete confirm ── */}
            <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Eliminar widget</DialogTitle>
                        <DialogDescription>Esta acción no se puede deshacer. El código embed de este widget dejará de funcionar.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={doDelete} data-testid="confirm-delete-widget">Eliminar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Preview dialog ── */}
            <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Vista previa: {preview?.name}</DialogTitle>
                        <DialogDescription className="font-mono text-xs break-all">{preview ? previewSrc(preview) : ''}</DialogDescription>
                    </DialogHeader>
                    <div className="rounded-xl border bg-secondary/30 p-3 flex justify-center overflow-auto">
                        {preview && (
                            <iframe
                                src={previewSrc(preview)}
                                width={preview.config?.width_unit === '%' ? '100%' : (preview.config?.width  || 380)}
                                height={preview.config?.height_unit === '%' ? 480    : (preview.config?.height || 520)}
                                style={{ border: 0, borderRadius: preview.config?.radius || 16, display: 'block', maxWidth: '100%' }}
                                title={`Preview: ${preview.name}`}
                                allow="clipboard-write"
                                referrerPolicy="no-referrer-when-downgrade"
                                loading="lazy"
                                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                                data-testid="widget-preview-iframe"
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Embed code dialog ── */}
            <Dialog open={!!embedFor} onOpenChange={() => setEmbedFor(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Código embebido: {embedFor?.name}</DialogTitle>
                        <DialogDescription>
                            Copia y pega en tu sitio. Todos los links apuntan a{' '}
                            <strong>{PLATFORM_URL}</strong>.
                        </DialogDescription>
                    </DialogHeader>

                    {embedFor && (
                        <Tabs defaultValue="iframe">
                            <TabsList>
                                <TabsTrigger value="iframe"  data-testid="embed-tab-iframe">iframe</TabsTrigger>
                                <TabsTrigger value="script"  data-testid="embed-tab-script">Script JS</TabsTrigger>
                                <TabsTrigger value="info"    data-testid="embed-tab-info">Información</TabsTrigger>
                            </TabsList>

                            {/* ── IFRAME TAB ── */}
                            <TabsContent value="iframe" className="space-y-3 pt-3">
                                <EmbedNotice type="iframe" />
                                <Textarea
                                    readOnly
                                    rows={8}
                                    className="font-mono text-xs bg-gray-950 text-green-300 border-gray-800"
                                    value={buildIframe(embedFor)}
                                    data-testid="embed-iframe-code"
                                    spellCheck={false}
                                />
                                <div className="flex gap-2">
                                    <Button onClick={() => copy(buildIframe(embedFor))} size="sm" className="bg-red-600 hover:bg-red-700" data-testid="embed-iframe-copy">
                                        <Copy className="h-4 w-4 mr-1" /> Copiar iframe
                                    </Button>
                                </div>
                                <EmbedValidation code={buildIframe(embedFor)} type="iframe" widget={embedFor} />
                            </TabsContent>

                            {/* ── SCRIPT TAB ── */}
                            <TabsContent value="script" className="space-y-3 pt-3">
                                <EmbedNotice type="script" />
                                <Textarea
                                    readOnly
                                    rows={14}
                                    className="font-mono text-xs bg-gray-950 text-green-300 border-gray-800"
                                    value={buildScript(embedFor)}
                                    data-testid="embed-script-code"
                                    spellCheck={false}
                                />
                                <div className="flex gap-2">
                                    <Button onClick={() => copy(buildScript(embedFor))} size="sm" className="bg-red-600 hover:bg-red-700" data-testid="embed-script-copy">
                                        <Copy className="h-4 w-4 mr-1" /> Copiar script
                                    </Button>
                                </div>
                                <EmbedValidation code={buildScript(embedFor)} type="script" widget={embedFor} />
                            </TabsContent>

                            {/* ── INFO TAB ── */}
                            <TabsContent value="info" className="pt-3 space-y-3 text-sm">
                                <div className="rounded-xl border bg-secondary/20 p-4 space-y-2">
                                    <p><span className="font-semibold">Widget ID:</span> <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{embedFor.id}</code></p>
                                    <p><span className="font-semibold">URL del widget:</span> <a href={`${PLATFORM_URL}/widget/${embedFor.id}`} target="_blank" rel="noreferrer" className="text-red-600 underline text-xs break-all">{PLATFORM_URL}/widget/{embedFor.id}</a></p>
                                    <p><span className="font-semibold">Tipo:</span> {WIDGET_TYPES.find(t => t.value === embedFor.widget_type)?.label}</p>
                                    <p><span className="font-semibold">Estado:</span> {embedFor.enabled ? <span className="text-green-600 font-medium">Activo</span> : <span className="text-gray-500">Inactivo</span>}</p>
                                    <p><span className="font-semibold">Dimensiones:</span>{' '}
                                        {dimStr(embedFor.config?.width ?? 380, embedFor.config?.width_unit || 'px')} ×{' '}
                                        {dimStr(embedFor.config?.height ?? 520, embedFor.config?.height_unit || 'px')}
                                    </p>
                                    <p><span className="font-semibold">Dominios permitidos:</span>{' '}
                                        {(embedFor.config?.allowed_domains || []).length > 0
                                            ? (embedFor.config.allowed_domains.join(', '))
                                            : <span className="text-gray-400">Todos (sin restricción)</span>
                                        }
                                    </p>
                                </div>
                            </TabsContent>
                        </Tabs>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ─── embed notice ─────────────────────────────────────────────────────────────

function EmbedNotice({ type }) {
    if (type === 'iframe') return (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
                El <strong>iframe</strong> es la opción más segura y compatible. Funciona en cualquier CMS (WordPress, Wix, Webflow, etc.).
                Pega el código donde quieras mostrar el widget.
            </span>
        </div>
    );
    return (
        <div className="flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
                El <strong>script</strong> es ideal cuando quieres control adicional desde JavaScript. Requiere que tu sitio permita scripts de terceros.
                El loader es idempotente (no se carga dos veces aunque pegues el código múltiples veces).
            </span>
        </div>
    );
}

// ─── embed validation panel ───────────────────────────────────────────────────

function EmbedValidation({ code, type, widget }) {
    const checks = useMemo(() => {
        const results = [];
        const url = `${PLATFORM_URL}/widget/${widget.id}`;

        // 1. Platform URL is correct
        results.push({
            ok:  code.includes(PLATFORM_URL),
            msg: `URL apunta a ${PLATFORM_URL}`,
        });

        // 2. Widget ID appears in code
        results.push({
            ok:  code.includes(String(widget.id || '')),
            msg: 'ID del widget incluido en el código',
        });

        // 3. No localhost or dev URLs leaked into code
        const hasLeak = code.includes('localhost') || code.includes('127.0.0.1') || code.includes('.replit.');
        results.push({
            ok:  !hasLeak,
            msg: 'Sin URLs de desarrollo/localhost',
        });

        // 4. iframe-specific checks
        if (type === 'iframe') {
            results.push({ ok: code.includes('sandbox='),         msg: 'Atributo sandbox presente (seguridad XSS)' });
            results.push({ ok: code.includes('referrerpolicy='),  msg: 'referrerpolicy configurado' });
            results.push({ ok: code.includes('loading="lazy"'),   msg: 'Carga lazy activada (rendimiento)' });
        }

        // 5. script-specific checks
        if (type === 'script') {
            results.push({ ok: code.includes('onerror'),    msg: 'Manejo de error de carga incluido' });
            results.push({ ok: code.includes('return'),     msg: 'Guard anti-doble-carga incluido' });
            results.push({ ok: code.includes('async'),      msg: 'Carga asíncrona (no bloquea el DOM)' });
        }

        return results;
    }, [code, type, widget]);

    const allOk = checks.every(c => c.ok);

    return (
        <div className={`rounded-xl border px-3 py-3 space-y-1.5 ${allOk ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
            <p className={`text-xs font-bold flex items-center gap-1.5 ${allOk ? 'text-green-700' : 'text-amber-700'}`}>
                {allOk
                    ? <><CheckCircle2 className="h-3.5 w-3.5" /> Código validado — listo para producción</>
                    : <><AlertTriangle className="h-3.5 w-3.5" /> Se encontraron problemas en el código</>
                }
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {checks.map((c, i) => (
                    <div key={i} className={`text-[11px] flex items-center gap-1 ${c.ok ? 'text-green-700' : 'text-amber-700 font-semibold'}`}>
                        {c.ok ? '✓' : '✗'} {c.msg}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── widget editor dialog ─────────────────────────────────────────────────────

function WidgetEditor({ open, value, onChange, onSave, users, categories }) {
    const w = value;
    if (!w) return null;
    const typeSpec = WIDGET_TYPES.find(t => t.value === w.widget_type);
    const cfg = w.config || {};

    const setCfg      = (patch) => onChange({ ...w, config: { ...cfg, ...patch } });
    const setFilters  = (patch) => setCfg({ filters: { ...(cfg.filters || {}), ...patch } });

    const widthUnit  = cfg.width_unit  || 'px';
    const heightUnit = cfg.height_unit || 'px';
    const widthMax   = widthUnit  === 'px' ? 2000 : 100;
    const heightMax  = heightUnit === 'px' ? 4000 : 100;

    return (
        <Dialog open={open} onOpenChange={() => onChange(null)}>
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="widget-editor">
                <DialogHeader>
                    <DialogTitle>{w.id ? 'Editar widget' : 'Nuevo widget'}</DialogTitle>
                    <DialogDescription>Configura tipo, origen y opciones avanzadas.</DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="basic">
                    <TabsList className="flex-wrap">
                        <TabsTrigger value="basic"     data-testid="tab-basic">Básico</TabsTrigger>
                        <TabsTrigger value="style"     data-testid="tab-style">Estilo y dimensiones</TabsTrigger>
                        <TabsTrigger value="behavior"  data-testid="tab-behavior">Comportamiento</TabsTrigger>
                        <TabsTrigger value="filters"   data-testid="tab-filters">Filtros</TabsTrigger>
                        <TabsTrigger value="security"  data-testid="tab-security">Seguridad</TabsTrigger>
                    </TabsList>

                    {/* ── BASIC ── */}
                    <TabsContent value="basic" className="space-y-3 pt-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Nombre *</Label>
                                <Input value={w.name} onChange={(e) => onChange({ ...w, name: e.target.value })} data-testid="w-name" />
                            </div>
                            <div className="flex items-end gap-2">
                                <Switch checked={!!w.enabled} onCheckedChange={(v) => onChange({ ...w, enabled: v })} data-testid="w-enabled" />
                                <Label>Habilitado</Label>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Tipo de widget *</Label>
                                <Select value={w.widget_type} onValueChange={(v) => onChange({ ...w, widget_type: v })}>
                                    <SelectTrigger data-testid="w-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {WIDGET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Origen *</Label>
                                <Select value={w.origin} onValueChange={(v) => onChange({ ...w, origin: v })}>
                                    <SelectTrigger data-testid="w-origin"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ORIGINS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {typeSpec?.needs_user && (
                            <div>
                                <Label>Usuario objetivo *</Label>
                                <Select value={w.target_user_id || '__none__'} onValueChange={(v) => onChange({ ...w, target_user_id: v === '__none__' ? null : v })}>
                                    <SelectTrigger data-testid="w-user"><SelectValue placeholder="Selecciona un usuario…" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— Seleccionar —</SelectItem>
                                        {users.slice(0, 200).map(u => (
                                            <SelectItem key={u.id} value={u.id}>@{u.nickname}{u.full_name ? ` – ${u.full_name}` : ''}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {typeSpec?.needs_ad && (
                            <div>
                                <Label>ID del anuncio *</Label>
                                <Input placeholder="uuid del anuncio" value={w.target_ad_id || ''} onChange={(e) => onChange({ ...w, target_ad_id: e.target.value })} data-testid="w-ad" />
                            </div>
                        )}
                        <div>
                            <Label>Descripción interna</Label>
                            <Textarea rows={2} value={w.description || ''} onChange={(e) => onChange({ ...w, description: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-end gap-2">
                                <Switch checked={!!cfg.show_title} onCheckedChange={(v) => setCfg({ show_title: v })} />
                                <Label>Mostrar título</Label>
                            </div>
                            <div>
                                <Label>Título visible</Label>
                                <Input value={cfg.title || ''} onChange={(e) => setCfg({ title: e.target.value })} placeholder="(opcional)" />
                            </div>
                        </div>
                    </TabsContent>

                    {/* ── STYLE & DIMENSIONS ── */}
                    <TabsContent value="style" className="space-y-4 pt-3">
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label>Tema</Label>
                                <Select value={cfg.theme || 'light'} onValueChange={(v) => setCfg({ theme: v })}>
                                    <SelectTrigger data-testid="cfg-theme"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="light">Claro</SelectItem>
                                        <SelectItem value="dark">Oscuro</SelectItem>
                                        <SelectItem value="auto">Automático</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Color primario</Label>
                                <Input type="color" value={cfg.primary_color || '#dc2626'} onChange={(e) => setCfg({ primary_color: e.target.value })} className="h-10 p-1" data-testid="cfg-color" />
                            </div>
                            <div>
                                <Label>Radio borde: {cfg.radius ?? 16}px</Label>
                                <Slider min={0} max={32} step={1} value={[cfg.radius ?? 16]} onValueChange={(v) => setCfg({ radius: v[0] })} />
                            </div>
                        </div>

                        {/* Dimensions with unit selector */}
                        <div className="rounded-xl border bg-secondary/20 p-4 space-y-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Dimensiones</p>

                            {/* Width */}
                            <div className="space-y-1.5">
                                <Label>
                                    Ancho —{' '}
                                    <span className="font-mono text-red-600">{dimStr(cfg.width ?? 380, widthUnit)}</span>
                                </Label>
                                <div className="flex gap-2">
                                    <Input
                                        type="number"
                                        min={1}
                                        max={widthMax}
                                        value={cfg.width ?? 380}
                                        onChange={(e) => setCfg({ width: clamp(Number(e.target.value), 1, widthMax) })}
                                        className="flex-1"
                                        data-testid="cfg-width"
                                    />
                                    <Select value={widthUnit} onValueChange={(v) => setCfg({ width_unit: v, width: v === 'px' ? 380 : 100 })} data-testid="cfg-width-unit">
                                        <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {DIM_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <p className="text-[11px] text-muted-foreground">
                                    {widthUnit === 'px' && 'Valor fijo en píxeles. Máx. 2000px.'}
                                    {widthUnit === '%'  && 'Porcentaje del contenedor padre. Máx. 100%.'}
                                    {widthUnit === 'vw' && 'Porcentaje del ancho de la ventana del navegador.'}
                                    {widthUnit === 'vh' && 'Porcentaje del alto de la ventana del navegador.'}
                                </p>
                            </div>

                            {/* Height */}
                            <div className="space-y-1.5">
                                <Label>
                                    Alto —{' '}
                                    <span className="font-mono text-red-600">{dimStr(cfg.height ?? 520, heightUnit)}</span>
                                </Label>
                                <div className="flex gap-2">
                                    <Input
                                        type="number"
                                        min={1}
                                        max={heightMax}
                                        value={cfg.height ?? 520}
                                        onChange={(e) => setCfg({ height: clamp(Number(e.target.value), 1, heightMax) })}
                                        className="flex-1"
                                        data-testid="cfg-height"
                                    />
                                    <Select value={heightUnit} onValueChange={(v) => setCfg({ height_unit: v, height: v === 'px' ? 520 : 80 })} data-testid="cfg-height-unit">
                                        <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {DIM_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <p className="text-[11px] text-muted-foreground">
                                    {heightUnit === 'px' && 'Valor fijo en píxeles. Máx. 4000px.'}
                                    {heightUnit === '%'  && 'Porcentaje del contenedor padre (el padre debe tener alto definido).'}
                                    {heightUnit === 'vh' && 'Porcentaje del alto de la ventana. Ej: 80vh = 80% de la pantalla.'}
                                    {heightUnit === 'vw' && 'Porcentaje del ancho de la ventana (inusual para alto, pero válido).'}
                                </p>
                            </div>

                            {/* Live preview of generated dimension */}
                            <div className="rounded-lg bg-gray-950 text-green-300 font-mono text-xs p-3">
                                <span className="text-gray-500">width=</span>"{dimStr(cfg.width ?? 380, widthUnit)}"{'  '}
                                <span className="text-gray-500">height=</span>"{dimStr(cfg.height ?? 520, heightUnit)}"
                            </div>
                        </div>
                    </TabsContent>

                    {/* ── BEHAVIOR ── */}
                    <TabsContent value="behavior" className="space-y-4 pt-3">
                        {/* Items count */}
                        <div>
                            <Label>Cantidad de items: {cfg.items || 6}</Label>
                            <Slider min={1} max={24} step={1} value={[cfg.items || 6]} onValueChange={(v) => setCfg({ items: v[0] })} />
                        </div>

                        {/* Ad carousel options */}
                        {typeSpec?.is_ad && (
                            <div className="rounded-xl border bg-secondary/20 p-4 space-y-3">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Opciones de carrusel de anuncios</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={!!cfg.show_logo}
                                            onCheckedChange={(v) => setCfg({ show_logo: v })}
                                            data-testid="cfg-show-logo"
                                        />
                                        <div>
                                            <Label className="leading-tight">Logo OkLoVendo en header</Label>
                                            <p className="text-[10px] text-muted-foreground">Muestra el logo de la plataforma al tope del widget</p>
                                        </div>
                                    </div>
                                    {w.widget_type !== 'ad_carousel' && (
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={!!cfg.carousel}
                                                onCheckedChange={(v) => setCfg({ carousel: v })}
                                                data-testid="cfg-carousel"
                                            />
                                            <div>
                                                <Label className="leading-tight">Modo carrusel horizontal</Label>
                                                <p className="text-[10px] text-muted-foreground">Desactivo = grilla 2 columnas</p>
                                            </div>
                                        </div>
                                    )}
                                    {w.widget_type === 'ad_carousel' && (
                                        <div className="rounded-lg bg-blue-50 border border-blue-200 p-2 text-xs text-blue-700">
                                            Este tipo siempre es carrusel horizontal con formato AdCard (4:5).
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* User carousel options */}
                        {typeSpec?.is_user_carousel && (
                            <div className="rounded-xl border bg-secondary/20 p-4 space-y-3">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Opciones de carrusel de usuarios</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Orientación</Label>
                                        <Select
                                            value={cfg.carousel_orientation || 'horizontal'}
                                            onValueChange={(v) => setCfg({ carousel_orientation: v })}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="horizontal">Horizontal (scroll)</SelectItem>
                                                <SelectItem value="vertical">Vertical (lista)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Tipo de usuarios</Label>
                                        <Select
                                            value={cfg.user_carousel_kind || 'new'}
                                            onValueChange={(v) => setCfg({ user_carousel_kind: v })}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="new">Más recientes</SelectItem>
                                                <SelectItem value="top_rated">Mejor calificados</SelectItem>
                                                <SelectItem value="verified">Verificados (KYC)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Autoplay (for non-user-carousel) */}
                        {!typeSpec?.is_user_carousel && (
                            <div className="flex items-center gap-2">
                                <Switch checked={!!cfg.autoplay} onCheckedChange={(v) => setCfg({ autoplay: v })} data-testid="cfg-autoplay" />
                                <Label>Autoplay</Label>
                            </div>
                        )}

                        {/* CTA */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Texto del botón CTA</Label>
                                <Input value={cfg.cta_label || ''} onChange={(e) => setCfg({ cta_label: e.target.value })} placeholder="Ver más" />
                            </div>
                            <div>
                                <Label>URL del botón CTA</Label>
                                <Input
                                    value={cfg.cta_url || ''}
                                    onChange={(e) => setCfg({ cta_url: e.target.value })}
                                    placeholder="https://oklovendo.okgo.mx/…"
                                />
                            </div>
                        </div>
                    </TabsContent>

                    {/* ── FILTERS ── */}
                    <TabsContent value="filters" className="space-y-3 pt-3">
                        <div>
                            <Label>Categoría</Label>
                            <Select value={cfg.filters?.category || 'all'} onValueChange={(v) => setFilters({ category: v === 'all' ? null : v })}>
                                <SelectTrigger data-testid="cfg-cat"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas</SelectItem>
                                    {categories.map(c => (
                                        <SelectItem key={c.id || c.key || c.name} value={c.key || c.id || c.name}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Precio mínimo</Label>
                                <Input type="number" value={cfg.filters?.min_price ?? ''} onChange={(e) => setFilters({ min_price: e.target.value ? Number(e.target.value) : null })} />
                            </div>
                            <div>
                                <Label>Precio máximo</Label>
                                <Input type="number" value={cfg.filters?.max_price ?? ''} onChange={(e) => setFilters({ max_price: e.target.value ? Number(e.target.value) : null })} />
                            </div>
                        </div>
                    </TabsContent>

                    {/* ── SECURITY ── */}
                    <TabsContent value="security" className="space-y-3 pt-3">
                        <div>
                            <Label>Dominios permitidos (uno por línea)</Label>
                            <Textarea
                                rows={4}
                                placeholder={"ejemplo.com\nsubdominio.otro.com\n(vacío = permitir todos)"}
                                value={(cfg.allowed_domains || []).join('\n')}
                                onChange={(e) => setCfg({ allowed_domains: e.target.value.split('\n').map(x => x.trim()).filter(Boolean) })}
                                data-testid="cfg-domains"
                            />
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Se valida el header <code>Referer</code>/<code>Origin</code> al servir el widget. Dejar vacío permite embeber desde cualquier dominio.
                            </p>
                        </div>
                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
                            <p className="font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Nota de seguridad</p>
                            <p>El iframe generado incluye el atributo <code>sandbox="allow-scripts allow-same-origin allow-popups allow-forms"</code> para prevenir ataques XSS y clickjacking desde el sitio embebedor.</p>
                            <p>Si restringes dominios, asegúrate de incluir el dominio exacto (sin <code>http://</code> ni barra final). Ejemplo: <code>mitienda.com</code></p>
                        </div>
                    </TabsContent>
                </Tabs>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onChange(null)}>Cancelar</Button>
                    <Button onClick={onSave} className="bg-red-600 hover:bg-red-700" data-testid="save-widget">
                        {w.id ? 'Actualizar widget' : 'Crear widget'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
